import {
    Request,
    RequestSource,
    RequestStatus,
    Conflict,
    ConflictSeverity,
    ShiftPreference,
    WeekendPattern,
} from '@features/requests/types';
import { RequestModel } from '../models';
import { WEEKEND_PATTERNS, SHIFT_LIMITS } from '../constants';
import { formatDate } from './validationUtils';

/**
 * Conflict detection utilities
 */

/**
 * Checks if two requests have overlapping date ranges
 */
export function checkDateOverlap(
    request1: Request,
    request2: Request,
): boolean {
    if (
        !request1.startDate ||
        !request1.endDate ||
        !request2.startDate ||
        !request2.endDate
    ) {
        return false;
    }
    return RequestModel.datesOverlap(
        request1.startDate,
        request1.endDate,
        request2.startDate,
        request2.endDate,
    );
}

/**
 * Gets all requests that block dates for new requests
 * Only DELIVERED and APPROVED requests block dates
 */
export function getBlockingRequests(
    employeeId: string,
    existingRequests: Request[],
): Request[] {
    return existingRequests.filter(
        (req): boolean =>
            req.employeeId === employeeId && RequestModel.isBlocking(req),
    );
}

/**
 * Checks for conflicts with weekend patterns
 */
export function checkWeekendPatternConflicts(
    startDate: Date,
    endDate: Date,
    patterns: WeekendPattern[] = WEEKEND_PATTERNS,
): Conflict[] {
    const conflicts: Conflict[] = [];

    // Check each day in the date range
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay();

        // Check if this day is blocked by any pattern
        for (const pattern of patterns) {
            if (pattern.dayOfWeek === dayOfWeek && pattern.isBlocked) {
                conflicts.push({
                    type: 'WEEKEND_PATTERN',
                    severity: ConflictSeverity.ERROR,
                    message: `Du kan ikke søke for ${formatDate(currentDate)}: dag blokkert av helgemønster`,
                    date: new Date(currentDate),
                });
            }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return conflicts;
}

/**
 * Calculates hours for a shift preference request
 * For this MVP, we assume:
 * - Morning shift = 8 hours
 * - Afternoon shift = 8 hours
 * - Night shift = 8 hours
 */
function getShiftHours(): number {
    // All shifts are 8 hours for this MVP
    return 8;
}

/**
 * Gets the week number for a date
 */
function getWeekNumber(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // Copy date so don't modify original
    d.setDate(d.getDate() + 4 - d.getDay()); // Thursday
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Checks for conflicts with shift hours limit
 * This checks if the new request would cause the employee to exceed the weekly hours limit
 */
export function checkShiftHoursLimit(
    employeeId: string,
    startDate: Date,
    endDate: Date,
    shiftPreference: ShiftPreference,
    existingRequests: Request[],
    maxHoursPerWeek: number = SHIFT_LIMITS.maxHoursPerWeek,
): Conflict[] {
    const conflicts: Conflict[] = [];

    // This check only applies to SHIFT_PREFERENCE requests
    if (!shiftPreference) {
        return conflicts;
    }

    const shiftHours = getShiftHours();

    // Get all days in the date range
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // Track hours per week - use string keys for Map
    interface WeekHours {
        week: number;
        year: number;
        hours: number;
    }

    const weeklyHours: Map<string, WeekHours> = new Map();

    // Add hours from existing SHIFT_PREFERENCE requests
    for (const req of existingRequests) {
        if (
            req.employeeId === employeeId &&
            req.type === RequestSource.WISH &&
            req.status !== RequestStatus.CANCELLED &&
            req.shiftPreference &&
            req.startDate &&
            req.endDate
        ) {
            const reqStart = new Date(req.startDate);
            reqStart.setHours(0, 0, 0, 0);
            const reqEnd = new Date(req.endDate);
            reqEnd.setHours(0, 0, 0, 0);

            let dayIter = new Date(reqStart);
            while (dayIter <= reqEnd) {
                const weekNumber = getWeekNumber(dayIter);
                const year = dayIter.getFullYear();
                const key = `${year}_${weekNumber}`;

                const existing = weeklyHours.get(key) ?? {
                    week: weekNumber,
                    year,
                    hours: 0,
                };
                const updated: WeekHours = {
                    ...existing,
                    hours: existing.hours + getShiftHours(),
                };
                weeklyHours.set(key, updated);

                dayIter.setDate(dayIter.getDate() + 1);
            }
        }
    }

    // Add hours from the new request
    let day = new Date(currentDate);
    let totalWarningHours = 0;

    while (day <= end) {
        const weekNumber = getWeekNumber(day);
        const year = day.getFullYear();
        const key = `${year}_${weekNumber}`;

        const existing = weeklyHours.get(key) ?? {
            week: weekNumber,
            year,
            hours: 0,
        };
        const newHours = existing.hours + shiftHours;

        // Update the map
        const updated: WeekHours = { week: weekNumber, year, hours: newHours };
        weeklyHours.set(key, updated);

        // Check if we exceed the limit
        if (newHours > maxHoursPerWeek) {
            // This is a warning, not an error - user can still submit with confirmation
            if (newHours > totalWarningHours) {
                totalWarningHours = newHours;
            }
        }

        day.setDate(day.getDate() + 1);
    }

    // If we have any weeks exceeding the limit, add a warning
    if (totalWarningHours > maxHoursPerWeek) {
        conflicts.push({
            type: 'SHIFT_HOURS_LIMIT',
            severity: ConflictSeverity.WARNING,
            message: `Advarsel: Du overskrider ${maxHoursPerWeek} timer per uke med denne søknaden.`,
        });
    }

    return conflicts;
}

/**
 * Checks all types of conflicts for a new request
 */
export function checkAllConflicts(
    employeeId: string,
    newRequest: {
        startDate: Date;
        endDate: Date;
        type: RequestSource;
        shiftPreference?: ShiftPreference;
    },
    existingRequests: Request[],
): Conflict[] {
    const allConflicts: Conflict[] = [];

    // Check date overlap with blocking requests
    const blockingRequests = getBlockingRequests(employeeId, existingRequests);
    for (const req of blockingRequests) {
        if (
            req.startDate &&
            req.endDate &&
            checkDateOverlap(req, newRequest as Request)
        ) {
            allConflicts.push({
                type: 'DATE_OVERLAP',
                severity: ConflictSeverity.ERROR,
                message: `Du har allerede en ${req.status.toLowerCase()} søknad for perioden ${formatDate(req.startDate)} - ${formatDate(req.endDate)}`,
                conflictingRequestId: req.id,
            });
        }
    }

    // Check weekend pattern conflicts
    const weekendConflicts = checkWeekendPatternConflicts(
        newRequest.startDate,
        newRequest.endDate,
    );
    allConflicts.push(...weekendConflicts);

    // Check shift hours limit (only for SHIFT_PREFERENCE)
    if (newRequest.type === RequestSource.WISH && newRequest.shiftPreference) {
        const shiftConflicts = checkShiftHoursLimit(
            employeeId,
            newRequest.startDate,
            newRequest.endDate,
            newRequest.shiftPreference,
            existingRequests,
        );
        allConflicts.push(...shiftConflicts);
    }

    return allConflicts;
}

/**
 * Separates conflicts by severity
 */
export function separateConflictsBySeverity(conflicts: Conflict[]): {
    errors: Conflict[];
    warnings: Conflict[];
} {
    const errors: Conflict[] = [];
    const warnings: Conflict[] = [];

    for (const conflict of conflicts) {
        if (conflict.severity === ConflictSeverity.ERROR) {
            errors.push(conflict);
        } else if (conflict.severity === ConflictSeverity.WARNING) {
            warnings.push(conflict);
        }
    }

    return { errors, warnings };
}

/**
 * Checks if there are any blocking conflicts (errors)
 */
export function hasBlockingConflicts(conflicts: Conflict[]): boolean {
    return conflicts.some(
        (c): boolean => c.severity === ConflictSeverity.ERROR,
    );
}
